<template>
    <div
        class="banner-carousel"
        :class="{ 'fixed-show-arrows': props.fixedShowArrows }"
        :style="containerStyle"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <div class="carousel-wrapper">
            <div
                class="carousel-track"
                :style="trackStyle"
            >
                <div
                    v-for="(item, index) in extendedList"
                    :key="`slide-${index}`"
                    class="carousel-slide"
                    :class="{
                        'is-active': isLoopEnabled ? index === trackIndex : index === currentIndex,
                        'has-link': item.link,
                        'no-link': !item.link,
                        'is-video': item.resource_type === 'video',
                    }"
                    :style="slideStyle"
                >
                    <a
                        :href="getItemLink(item)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="banner-link"
                        @click="handleBannerClick(item, $event)"
                    >
                        <!-- 图片类型 -->
                        <template v-if="item.resource_type === 'image'">
                            <!-- 懒加载：只加载当前项和相邻项的图片 -->
                            <img
                                v-if="shouldLoadImage(index)"
                                :src="item.banner_url"
                                loading="eager"
                                alt="banner"
                                class="banner-media"
                            />
                            <!-- 占位符：未加载的图片显示骨架屏 -->
                            <div
                                v-else
                                class="banner-placeholder"
                            />
                        </template>
                        <!-- 视频类型 -->
                        <video
                            v-else-if="item.resource_type === 'video'"
                            ref="videoRefs"
                            :src="item.banner_url"
                            :data-video-index="index"
                            class="banner-media banner-video"
                            preload="metadata"
                            muted
                            playsinline
                            @play="handleVideoPlay"
                            @pause="handleVideoPause"
                            @ended="handleVideoEnded"
                        />
                    </a>
                </div>
            </div>
        </div>

        <!-- 箭头 -->
        <template v-if="showArrows && bannerList.length > 1">
            <button
                type="button"
                class="carousel-arrow carousel-arrow-prev"
                @click="prev"
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                >
                    <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
            </button>
            <button
                type="button"
                class="carousel-arrow carousel-arrow-next"
                @click="next"
            >
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                >
                    <polyline points="9 6 15 12 9 18"></polyline>
                </svg>
            </button>
        </template>

        <!-- 指示点 -->
        <div
            v-if="showDots && bannerList.length > 1"
            class="carousel-dots"
        >
            <button
                v-for="(_, index) in bannerList"
                :key="index"
                type="button"
                class="carousel-dot"
                :class="{ 'is-active': index === currentIndex }"
                @click="goTo(index)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
    import type { BannerCarouselEmits, BannerCarouselProps, BannerItem } from './banner-carousel.types'

    defineOptions({ name: 's-banner-carousel' })

    /**
     * @component BannerCarousel
     * @description 全栏轮播Banner组件，支持图片和视频的无限滚动
     * @example
     *   <BannerCarousel
     *     :banner-list="bannerData"
     *     :autoplay="true"
     *     :autoplay-speed="3000"
     *   />
     */

    const props = withDefaults(defineProps<BannerCarouselProps>(), {
        bannerList: () => [],
        autoplay: true,
        showDots: true,
        autoplaySpeed: 3000,
        effect: 'slide',
        pauseOnHover: true,
        fixedShowArrows: false,
        showArrows: true,
        height: 470,
        borderRadius: 12,
        getLinkUrl: undefined,
        getDescription: undefined,
        loop: true,
    })

    const emit = defineEmits<BannerCarouselEmits>()

    // 当前活跃索引（真实数据的索引）
    const currentIndex = ref(0)
    // 轨道索引（包含克隆项的索引，用于 translateX 计算）
    const trackIndex = ref(0)
    // 是否正在进行边界跳转（禁用动画）
    const isJumping = ref(false)
    // 控制自动播放状态
    const isAutoPlaying = ref(props.autoplay)
    // 视频是否正在播放
    const isVideoPlaying = ref(false)
    // 自动播放定时器
    let autoplayTimer: ReturnType<typeof setInterval> | null = null
    // 视频引用
    const videoRefs = ref<HTMLVideoElement[]>([])

    // 是否启用无限循环（slide 效果 + 多于 1 项 + loop 开启）
    const isLoopEnabled = computed(() => {
        return props.loop && props.effect === 'slide' && props.bannerList.length > 1
    })

    // 扩展后的轮播列表（首尾各克隆一项）
    const extendedList = computed(() => {
        if (!isLoopEnabled.value) {
            return props.bannerList
        }
        const list = props.bannerList
        // [最后一项, ...原始列表, 第一项]
        return [list[list.length - 1], ...list, list[0]]
    })

    // 容器样式
    const containerStyle = computed(() => ({
        '--carousel-height': typeof props.height === 'number' ? `${props.height}px` : props.height,
        '--carousel-radius': typeof props.borderRadius === 'number' ? `${props.borderRadius}px` : props.borderRadius,
    }))

    // 轨道样式
    const trackStyle = computed(() => {
        if (props.effect === 'slide') {
            const index = isLoopEnabled.value ? trackIndex.value : currentIndex.value
            return {
                transform: `translateX(-${index * 100}%)`,
                transition: isJumping.value ? 'none' : 'transform 0.5s ease-in-out',
            }
        }
        return {}
    })

    // 单个slide样式（用于fade效果）
    const slideStyle = computed(() => {
        if (props.effect === 'fade') {
            return {
                position: 'absolute' as const,
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
            }
        }
        return {}
    })

    /**
     * @function shouldLoadImage
     * @description 判断是否应该加载该索引的图片（懒加载优化）
     * 只加载当前项和相邻项的图片，减少内存占用
     */
    const shouldLoadImage = (index: number): boolean => {
        const currentIdx = isLoopEnabled.value ? trackIndex.value : currentIndex.value
        // 加载当前项、前一项、后一项（共3项）
        const diff = Math.abs(index - currentIdx)
        return diff <= 1 || diff >= extendedList.value.length - 1
    }

    /**
     * @function getItemLink
     * @description 获取Banner跳转链接
     */
    const getItemLink = (item: BannerItem): string => {
        if (props.getLinkUrl) {
            return props.getLinkUrl(item)
        }
        return item.link || 'javascript:void(0)'
    }

    /**
     * @function getItemDescription
     * @description 获取Banner描述
     */
    const getItemDescription = (item: BannerItem): string => {
        if (props.getDescription) {
            return props.getDescription(item)
        }
        return item.description || ''
    }

    /**
     * @function getActiveVideo
     * @description 获取当前激活的视频元素
     */
    const getActiveVideo = (): HTMLVideoElement | null => {
        const currentItem = props.bannerList[currentIndex.value]
        if (currentItem?.resource_type !== 'video') return null

        const videos = document.querySelectorAll<HTMLVideoElement>('.banner-carousel .banner-video')
        // 在 loop 模式下，使用 trackIndex 获取视频（因为 extendedList 有克隆项）
        const videoIndex = isLoopEnabled.value ? trackIndex.value : currentIndex.value
        return videos[videoIndex] || null
    }

    /**
     * @function pauseActiveVideo
     * @description 暂停当前激活的视频
     */
    const pauseActiveVideo = () => {
        const video = getActiveVideo()
        if (video && !video.paused) {
            video.pause()
        }
    }

    /**
     * @function playActiveVideo
     * @description 播放当前激活的视频
     */
    const playActiveVideo = (resetTime = false) => {
        const video = getActiveVideo()
        if (video) {
            if (resetTime) {
                video.currentTime = 0
            }
            video.play().catch(error => {
                console.warn('视频播放失败:', error)
            })
        }
    }

    /**
     * @function startAutoplay
     * @description 启动自动播放
     */
    const startAutoplay = () => {
        if (!props.autoplay || props.bannerList.length <= 1) return

        stopAutoplay()
        autoplayTimer = setInterval(() => {
            if (isAutoPlaying.value && !isVideoPlaying.value) {
                next()
            }
        }, props.autoplaySpeed)
    }

    /**
     * @function stopAutoplay
     * @description 停止自动播放
     */
    const stopAutoplay = () => {
        if (autoplayTimer) {
            clearInterval(autoplayTimer)
            autoplayTimer = null
        }
    }

    /**
     * @function next
     * @description 切换到下一张
     */
    const next = () => {
        pauseActiveVideo()

        if (isLoopEnabled.value) {
            trackIndex.value++
            const nextRealIndex = (currentIndex.value + 1) % props.bannerList.length
            currentIndex.value = nextRealIndex
            emit('change', nextRealIndex)

            // 到达克隆的第一项（extendedList 的最后一项），需要跳转回真实的第一项
            if (trackIndex.value >= extendedList.value.length - 1) {
                setTimeout(() => {
                    isJumping.value = true
                    trackIndex.value = 1 // 跳转到真实的第一项（索引 1）
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            isJumping.value = false
                        })
                    })
                }, 500) // 等待动画完成
            }

            // 处理视频播放
            handleSlideChange(nextRealIndex)
        } else {
            const nextIndex = (currentIndex.value + 1) % props.bannerList.length
            goTo(nextIndex)
        }
    }

    /**
     * @function prev
     * @description 切换到上一张
     */
    const prev = () => {
        pauseActiveVideo()

        if (isLoopEnabled.value) {
            trackIndex.value--
            const prevRealIndex = (currentIndex.value - 1 + props.bannerList.length) % props.bannerList.length
            currentIndex.value = prevRealIndex
            emit('change', prevRealIndex)

            // 到达克隆的最后一项（extendedList 的第一项），需要跳转回真实的最后一项
            if (trackIndex.value <= 0) {
                setTimeout(() => {
                    isJumping.value = true
                    trackIndex.value = props.bannerList.length // 跳转到真实的最后一项
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            isJumping.value = false
                        })
                    })
                }, 500) // 等待动画完成
            }

            // 处理视频播放
            handleSlideChange(prevRealIndex)
        } else {
            const prevIndex = (currentIndex.value - 1 + props.bannerList.length) % props.bannerList.length
            goTo(prevIndex)
        }
    }

    /**
     * @function handleSlideChange
     * @description 处理 slide 切换后的视频播放
     */
    const handleSlideChange = (index: number) => {
        const currentItem = props.bannerList[index]
        if (currentItem?.resource_type === 'video') {
            setTimeout(() => {
                playActiveVideo(true)
            }, 500)
        }
    }

    /**
     * @function goTo
     * @description 跳转到指定索引
     */
    const goTo = (index: number) => {
        if (index < 0 || index >= props.bannerList.length) return

        currentIndex.value = index

        // 如果启用了无限循环，同步更新 trackIndex
        if (isLoopEnabled.value) {
            trackIndex.value = index + 1 // extendedList 中真实数据从索引 1 开始
        }

        emit('change', index)
        handleSlideChange(index)
    }

    /**
     * @function handleMouseEnter
     * @description 鼠标进入时暂停自动播放
     */
    const handleMouseEnter = () => {
        if (!props.pauseOnHover || !props.autoplay) return
        isAutoPlaying.value = false
        pauseActiveVideo()
    }

    /**
     * @function handleMouseLeave
     * @description 鼠标离开时恢复自动播放
     */
    const handleMouseLeave = () => {
        if (!props.pauseOnHover || !props.autoplay) return
        isAutoPlaying.value = true

        const currentItem = props.bannerList[currentIndex.value]
        if (currentItem?.resource_type === 'video') {
            playActiveVideo(false)
        }
    }

    /**
     * @function handleVideoPlay
     * @description 处理视频开始播放
     */
    const handleVideoPlay = () => {
        isVideoPlaying.value = true
        isAutoPlaying.value = false
    }

    /**
     * @function handleVideoPause
     * @description 处理视频暂停
     */
    const handleVideoPause = () => {
        isVideoPlaying.value = false
        if (props.autoplay) {
            isAutoPlaying.value = true
        }
    }

    /**
     * @function handleVideoEnded
     * @description 处理视频播放结束
     */
    const handleVideoEnded = () => {
        isVideoPlaying.value = false
        setTimeout(() => {
            next()
            if (props.autoplay) {
                isAutoPlaying.value = true
            }
        }, 500)
    }

    /**
     * @function handleBannerClick
     * @description 处理Banner点击
     */
    const handleBannerClick = (item: BannerItem, event: MouseEvent) => {
        emit('click', item, event)

        const link = getItemLink(item)
        if (!link || link === 'javascript:void(0)') {
            event.preventDefault()
            return
        }
    }

    // 监听bannerList变化
    watch(
        () => props.bannerList,
        () => {
            currentIndex.value = 0
            // 重置 trackIndex：如果启用循环，从 1 开始（跳过克隆的最后一项）
            trackIndex.value = isLoopEnabled.value ? 1 : 0
            startAutoplay()
        }
    )

    // 监听autoplay变化
    watch(
        () => props.autoplay,
        newVal => {
            isAutoPlaying.value = newVal
            if (newVal) {
                startAutoplay()
            } else {
                stopAutoplay()
            }
        }
    )

    onMounted(() => {
        // 初始化 trackIndex：如果启用循环，从 1 开始（跳过克隆的最后一项）
        if (isLoopEnabled.value) {
            trackIndex.value = 1
        }

        startAutoplay()

        // 如果第一张是视频则自动播放
        const firstItem = props.bannerList[0]
        if (firstItem?.resource_type === 'video') {
            setTimeout(() => {
                playActiveVideo(false)
            }, 100)
        }
    })

    onUnmounted(() => {
        stopAutoplay()
    })

    // 暴露方法供外部调用
    defineExpose({
        next,
        prev,
        goTo,
        currentIndex,
    })
</script>

<style scoped lang="scss">
    .banner-carousel {
        position: relative;
        width: 100%;
        height: var(--carousel-height, 470px);
        border-radius: var(--carousel-radius, 12px);
        overflow: hidden;
        user-select: none;

        .carousel-wrapper {
            width: 100%;
            height: 100%;
            overflow: hidden;
        }

        .carousel-track {
            display: flex;
            width: 100%;
            height: 100%;
            will-change: transform;
        }

        .carousel-slide {
            flex-shrink: 0;
            width: 100%;
            height: 100%;

            .banner-link {
                position: relative;
                display: flex;
                justify-content: center;
                align-items: center;
                width: 100%;
                height: 100%;
                background-color: #f5f5f5;
            }

            .banner-media {
                width: 100%;
                height: 100%;
                transition: transform 0.3s ease;
                object-fit: cover;
                will-change: transform;
            }

            .banner-placeholder {
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
                background-size: 200% 100%;
                animation: placeholder-shimmer 1.5s infinite;
            }

            .banner-video {
                pointer-events: none;
            }

            &:hover {
                .banner-media {
                    transform: scale(1.05);
                }
            }

            // 有链接时显示可点击样式
            &.has-link {
                cursor: pointer;

                .banner-link {
                    pointer-events: auto;

                    &:hover .banner-media:not(.banner-video) {
                        transform: scale(1.05);
                    }
                }
            }

            // 无链接时禁用点击
            &.no-link {
                cursor: default;

                .banner-link {
                    cursor: default;
                    pointer-events: none;
                }
            }

            .banner-description {
                position: absolute;
                bottom: 40px;
                left: 30px;
                width: calc(100% - 60px);
                font-size: 18px;
                font-weight: 500;
                text-align: left;
                text-overflow: ellipsis;
                white-space: nowrap;
                color: #fff;
                overflow: hidden;
            }
        }

        // 箭头样式
        .carousel-arrow {
            position: absolute;
            top: 50%;
            z-index: 10;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 36px;
            height: 36px;
            padding: 0;
            border: none;
            border-radius: 50%;
            background: rgba(118, 118, 118, 0.4);
            opacity: 0;
            transition: opacity 0.3s ease-in-out;
            transform: translateY(-50%);
            cursor: pointer;

            svg {
                width: 20px;
                height: 20px;
                color: #fff;
            }

            &:hover {
                background: rgba(118, 118, 118, 0.6);
            }
        }

        .carousel-arrow-prev {
            left: 20px;
        }

        .carousel-arrow-next {
            right: 20px;
        }

        // 悬停显示箭头
        &:hover .carousel-arrow {
            opacity: 1;
        }

        // 固定显示箭头
        &.fixed-show-arrows .carousel-arrow {
            opacity: 1;
        }

        // 指示点样式
        .carousel-dots {
            position: absolute;
            bottom: 20px;
            left: 50%;
            z-index: 10;
            display: flex;
            gap: 8px;
            transform: translateX(-50%);
        }

        .carousel-dot {
            width: 8px;
            height: 4px;
            padding: 0;
            border: none;
            border-radius: 2px;
            background: rgba(255, 255, 255, 0.5);
            transition: all 0.3s ease;
            cursor: pointer;

            &:hover {
                background: rgba(255, 255, 255, 0.8);
            }

            &.is-active {
                width: 24px;
                background: #fff;
            }
        }

        // 占位符闪烁动画
        @keyframes placeholder-shimmer {
            0% {
                background-position: -200% 0;
            }

            100% {
                background-position: 200% 0;
            }
        }
    }
</style>
